/**
 * Browser platform implementation — all DOM-specific code lives here.
 *
 * Provides terminal I/O via xterm.js (canvas/WebGL rendering),
 * audio via Web Audio API, and shell management via a <select> dropdown.
 */

import type { Platform, TerminalIO, AudioSink } from "./platform.js";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { WebglAddon } from "@xterm/addon-webgl";
import { packKeyboardEvent } from "./keyboard.js";
import { createTerminalQueryResponder } from "../../shared/terminal-responder.mjs";

const AUDIO_SAMPLE_RATE = 48_000;

// ── Terminal (xterm.js) ──────────────────────────────────────────────

class XTermTerminalIO implements TerminalIO {
  private terminal: Terminal;
  private fitAddon: FitAddon;
  private readonly container: HTMLElement;
  private readonly defaultDocumentTitle: string;
  private inputHandler: ((text: string) => void) | null = null;
  private keyboardHandler: ((packet: Uint8Array) => void) | null = null;
  private resizeHandler: ((cols: number, rows: number) => void) | null = null;
  private resizeListener: (() => void);
  private mouseDownListener: (event: MouseEvent) => void;
  private mouseUpListener: (event: MouseEvent) => void;
  private mouseTrackingMode: "none" | "vt200" | "drag" | "any" = "none";
  private sgrMouseEncoding = false;
  private mouseTrackingScanTail = "";
  private booted = false;
  private terminalQueryResponder = createTerminalQueryResponder();

  constructor(container: HTMLElement) {
    this.container = container;
    this.defaultDocumentTitle = document.title;
    this.terminal = new Terminal({
      cursorBlink: true,
      cursorStyle: "block",
      cursorInactiveStyle: "outline",
      fontSize: 13,
      fontFamily: "'SF Mono', 'Cascadia Code', 'Fira Code', 'Consolas', monospace",
      lineHeight: 1.25,
      scrollback: 5000,
      convertEol: true,
      theme: {
        background: "#111111",
        foreground: "#b0b0b0",
        cursor: "#d4d4d4",
        cursorAccent: "#111111",
        selectionBackground: "#264f78",
        black: "#000000",
        red: "#cd3131",
        green: "#0dbc79",
        yellow: "#e5e510",
        blue: "#2472c8",
        magenta: "#bc3fbc",
        cyan: "#11a8cd",
        white: "#e5e5e5",
        brightBlack: "#666666",
        brightRed: "#f14c4c",
        brightGreen: "#23d18b",
        brightYellow: "#f5f543",
        brightBlue: "#3b8eea",
        brightMagenta: "#d670d6",
        brightCyan: "#29b8db",
        brightWhite: "#ffffff",
      },
    });

    this.fitAddon = new FitAddon();
    this.terminal.loadAddon(this.fitAddon);
    this.terminal.open(container);

    // Try WebGL renderer for best performance, fall back to canvas
    try {
      const webgl = new WebglAddon();
      webgl.onContextLoss(() => {
        webgl.dispose();
      });
      this.terminal.loadAddon(webgl);
    } catch {
    }

    this.fitAddon.fit();

    this.terminal.onData((data) => {
      if (!this.booted) return;
      this.inputHandler?.(data);
    });

    this.terminal.onTitleChange((title) => {
      document.title = title.length > 0 ? title : this.defaultDocumentTitle;
    });

    // xterm.js onResize → kernel resize notification
    this.terminal.onResize(({ cols, rows }) => {
      this.resizeHandler?.(cols, rows);
    });

    // Preserve structured keyboard events for /dev/input/keyboard.
    // Returns true so xterm.js still processes the key for text input.
    this.terminal.attachCustomKeyEventHandler((e: KeyboardEvent) => {
      if (!this.booted || !this.keyboardHandler) return true;
      if (e.type === "keydown") {
        this.keyboardHandler(packKeyboardEvent(e, "down"));
      } else if (e.type === "keyup") {
        this.keyboardHandler(packKeyboardEvent(e, "up"));
      }
      return true;
    });

    // Window resize → fit (stored so we can remove on dispose)
    this.resizeListener = () => this.fitAddon.fit();
    window.addEventListener("resize", this.resizeListener, { passive: true });

    this.mouseDownListener = (event) => {
      this.forwardMouseEvent(event, false);
    };
    this.mouseUpListener = (event) => {
      this.forwardMouseEvent(event, true);
    };
    this.container.addEventListener("mousedown", this.mouseDownListener, { capture: true });
    this.container.addEventListener("mouseup", this.mouseUpListener, { capture: true });

    this.terminal.focus();
  }

  setBooted(): void {
    this.booted = true;
  }

  write(text: string): void {
    this.updateMouseTrackingState(text);
    this.terminal.write(text);
    const replies = this.terminalQueryResponder.feed(text);
    for (const reply of replies) {
      this.inputHandler?.(reply);
    }
  }

  resize(cols: number, rows: number): void {
    this.terminal.resize(cols, rows);
  }

  clear(): void {
    this.terminal.clear();
  }

  get size(): { cols: number; rows: number } {
    return { cols: this.terminal.cols, rows: this.terminal.rows };
  }

  onInput(handler: (text: string) => void): void {
    this.inputHandler = handler;
  }

  onKeyboardEvent(handler: (packet: Uint8Array) => void): void {
    this.keyboardHandler = handler;
  }

  onResize(handler: (cols: number, rows: number) => void): void {
    this.resizeHandler = handler;
  }

  setFocused(focused: boolean): void {
    if (focused) {
      this.terminal.focus();
    } else {
      this.terminal.blur();
    }
  }

  focus(): void {
    this.terminal.focus();
  }

  applyMeasuredSize(): void {
    this.fitAddon.fit();
  }

  setFontSizePx(px: number): void {
    const n = Number.isFinite(px) ? Math.round(px) : 13;
    const clamped = Math.min(32, Math.max(8, n));
    this.terminal.options.fontSize = clamped;
    this.fitAddon.fit();
  }

  getFontSizePx(): number {
    const n = this.terminal.options.fontSize;
    return typeof n === "number" && Number.isFinite(n) ? n : 13;
  }

  dispatchMouseEventForTest(
    col: number,
    row: number,
    opts?: { button?: number; released?: boolean; shiftKey?: boolean; altKey?: boolean; ctrlKey?: boolean; force?: boolean },
  ): void {
    if (!this.booted || !this.inputHandler) return;
    if (!opts?.force && (this.mouseTrackingMode === "none" || !this.sgrMouseEncoding)) return;

    const clampedCol = Math.min(this.terminal.cols, Math.max(1, Math.floor(col)));
    const clampedRow = Math.min(this.terminal.rows, Math.max(1, Math.floor(row)));
    this.inputHandler(this.encodeMouseEventForTest(clampedCol, clampedRow, opts));
  }

  encodeMouseEventForTest(
    col: number,
    row: number,
    opts?: { button?: number; released?: boolean; shiftKey?: boolean; altKey?: boolean; ctrlKey?: boolean },
  ): string {
    const released = opts?.released === true;
    const button = encodeMouseButton(opts?.button ?? 0);
    const modifiers =
      (opts?.shiftKey ? 4 : 0) |
      (opts?.altKey ? 8 : 0) |
      (opts?.ctrlKey ? 16 : 0);
    return `\x1b[<${button + modifiers};${col};${row}${released ? "m" : "M"}`;
  }

  dispose(): void {
    window.removeEventListener("resize", this.resizeListener);
    this.container.removeEventListener("mousedown", this.mouseDownListener, { capture: true });
    this.container.removeEventListener("mouseup", this.mouseUpListener, { capture: true });
    this.terminal.dispose();
  }

  private forwardMouseEvent(event: MouseEvent, released: boolean): void {
    if (!this.booted || !this.inputHandler) return;
    if (this.mouseTrackingMode === "none" || !this.sgrMouseEncoding) return;

    const rect = this.container.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    const col = Math.min(
      this.terminal.cols,
      Math.max(1, Math.floor(((event.clientX - rect.left) / rect.width) * this.terminal.cols) + 1),
    );
    const row = Math.min(
      this.terminal.rows,
      Math.max(1, Math.floor(((event.clientY - rect.top) / rect.height) * this.terminal.rows) + 1),
    );

    event.preventDefault();
    event.stopPropagation();
    this.terminal.focus();
    this.dispatchMouseEventForTest(col, row, {
      button: event.button,
      released,
      shiftKey: event.shiftKey,
      altKey: event.altKey || event.metaKey,
      ctrlKey: event.ctrlKey,
    });
  }

  private updateMouseTrackingState(text: string): void {
    const scan = `${this.mouseTrackingScanTail}${text}`;
    const matches = scan.matchAll(/\x1b\[\?(1000|1002|1003|1006)([hl])/g);
    for (const match of matches) {
      const code = match[1];
      const op = match[2];
      if (code === "1006") {
        this.sgrMouseEncoding = op === "h";
        continue;
      }
      if (op === "l") {
        this.mouseTrackingMode = "none";
        continue;
      }
      if (code === "1003") {
        this.mouseTrackingMode = "any";
      } else if (code === "1002") {
        this.mouseTrackingMode = "drag";
      } else {
        this.mouseTrackingMode = "vt200";
      }
    }
    this.mouseTrackingScanTail = scan.slice(-16);
  }
}

function encodeMouseButton(button: number): number {
  switch (button) {
    case 1:
      return 1;
    case 2:
      return 2;
    default:
      return 0;
  }
}

// ── Audio ─────────────────────────────────────────────────────────────

class BrowserAudioSink implements AudioSink {
  private audioCtx: AudioContext | null = null;
  private audioCursor = 0;
  private readyHandler: ((ready: boolean) => void) | null = null;

  get ready(): boolean {
    return this.audioCtx !== null && this.audioCtx.state === "running";
  }

  notifyReady(handler: (ready: boolean) => void): void {
    this.readyHandler = handler;
  }

  async activate(): Promise<void> {
    const AudioCtx = window.AudioContext;
    if (!AudioCtx) {
      this.readyHandler?.(false);
      return;
    }
    if (!this.audioCtx) {
      try {
        this.audioCtx = new AudioCtx({ sampleRate: AUDIO_SAMPLE_RATE });
      } catch {
        this.audioCtx = null;
        this.readyHandler?.(false);
        return;
      }
    }
    try {
      await this.audioCtx.resume();
      this.readyHandler?.(this.audioCtx.state === "running");
    } catch {
      this.readyHandler?.(false);
    }
  }

  playChunk(pcmBuf: ArrayBuffer): void {
    if (!this.audioCtx || !this.ready) return;
    const pcm16 = new Int16Array(pcmBuf);
    if (pcm16.length === 0) return;

    const samples = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) {
      samples[i] = pcm16[i] / 32768.0;
    }

    const buffer = this.audioCtx.createBuffer(1, samples.length, AUDIO_SAMPLE_RATE);
    buffer.copyToChannel(samples, 0);
    const src = this.audioCtx.createBufferSource();
    src.buffer = buffer;
    src.connect(this.audioCtx.destination);

    const startAt = Math.max(this.audioCtx.currentTime + 0.01, this.audioCursor);
    src.start(startAt);
    this.audioCursor = startAt + buffer.duration;
  }
}

// ── Browser Platform ──────────────────────────────────────────────────

export class BrowserPlatform implements Platform {
  readonly terminal: XTermTerminalIO;
  readonly audio: BrowserAudioSink;
  private statusEl: HTMLElement;
  private prereqEl: HTMLElement;
  private shellSelect: HTMLSelectElement;
  private shellChangeHandler: ((shell: string) => void) | null = null;

  constructor() {
    this.statusEl = document.getElementById("status")!;
    this.prereqEl = document.getElementById("prereqs")!;
    this.shellSelect = document.getElementById("shell-select") as HTMLSelectElement;
    const consoleEl = document.getElementById("console-output")!;

    this.terminal = new XTermTerminalIO(consoleEl);
    this.audio = new BrowserAudioSink();

    // Shell change
    this.shellSelect.addEventListener("change", () => {
      this.shellChangeHandler?.(this.shellSelect.value);
    });
  }

  async fetchBinary(path: string): Promise<ArrayBuffer> {
    const resp = await fetch(path);
    if (!resp.ok) {
      throw new Error(`failed to fetch ${path}: ${resp.status}`);
    }
    return resp.arrayBuffer();
  }

  setStatus(text: string, state?: "normal" | "pass" | "fail"): void {
    this.statusEl.textContent = text;
    if (state === "pass") {
      this.statusEl.className = "status pass";
    } else if (state === "fail") {
      this.statusEl.className = "status fail";
    } else {
      this.statusEl.className = "status";
    }
  }

  getShellChoice(): string {
    return this.shellSelect.value;
  }

  onShellChange(handler: (shell: string) => void): void {
    this.shellChangeHandler = handler;
  }

  onUserInteraction(handler: () => void): void {
    // Fire once on first user gesture, then auto-remove
    const onceHandler = () => {
      handler();
      document.removeEventListener("pointerdown", onceHandler);
      document.removeEventListener("keydown", onceHandler);
    };
    document.addEventListener("pointerdown", onceHandler, { passive: true });
    document.addEventListener("keydown", onceHandler, { passive: true });
  }

  checkPrerequisites(): { name: string; ok: boolean; detail: string }[] {
    return [
      {
        name: "Cross-Origin Isolation",
        ok: crossOriginIsolated,
        detail: crossOriginIsolated ? "Active" : "Missing COOP/COEP headers",
      },
      {
        name: "SharedArrayBuffer",
        ok: typeof SharedArrayBuffer !== "undefined",
        detail: typeof SharedArrayBuffer !== "undefined" ? "Available" : "Not available",
      },
      {
        name: "WebAssembly",
        ok: typeof WebAssembly !== "undefined",
        detail: typeof WebAssembly !== "undefined" ? "Available" : "Not available",
      },
    ];
  }

  renderPrerequisites(results: { name: string; ok: boolean; detail: string }[]): boolean {
    let allPassed = true;
    for (const r of results) {
      const row = document.createElement("div");
      row.className = "prereq-row";
      const icon = document.createElement("span");
      icon.className = r.ok ? "pass" : "fail";
      icon.textContent = r.ok ? "\u2713" : "\u2717";
      const label = document.createElement("strong");
      label.textContent = r.name;
      const detail = document.createElement("span");
      detail.className = r.ok ? "pass" : "fail";
      detail.textContent = r.detail;
      row.append(icon, " ", label, ": ", detail);
      this.prereqEl.appendChild(row);
      if (!r.ok) allPassed = false;
    }
    return allPassed;
  }
}
